import {
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	inject,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { EditorStatus } from '@components/documents-matter-editor/documents-matter-editor.component';
import {
	removeClickedFromAllTables,
	replaceClickedTable,
} from '@core/util/documents';
import { createId } from '@core/util/file';
import { FontInfo } from '@core/util/font';
import { getFullName } from '@core/util/string';
import { Font } from '@models/font';
import { Context } from '@models/matter';
import { DocumentLocation } from '@models/documents';
import { MatterDocumentType } from '@models/matter-file';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Actions, Select, Store, ofActionSuccessful } from '@ngxs/store';
import { SnackbarService } from '@services/ui/snackbar.service';
import { AuthState } from '@state/auth';
import { BusinessDispatch, BusinessState } from '@state/business';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { DocumentStylingState } from '@state/document-styling/document-styling.state';
import {
	FileUpload,
	FileUploadState,
} from '@state/file-upload/fileUpload.state';
import { CustomTags } from '@state/global/tags/tags.models';
import { TagsSelector } from '@state/global/tags/tags.selector';
import { InstanceState } from '@state/instance/instance.state';
import { MatterDispatch } from '@state/matter';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { UploadFileStatus } from '@state/utils/models';
import { EventObj } from '@tinymce/tinymce-angular/editor/Events';
import { Observable, Subject, merge, of } from 'rxjs';
import {
	debounceTime,
	distinctUntilChanged,
	filter,
	map,
	switchMap,
	take,
	takeUntil,
	tap,
} from 'rxjs/operators';
import {
	FlattenedCustomTags,
	TagsService,
} from 'src/app/shared/services/tags/tags.service';
import tinymce, { Editor } from 'tinymce';
import { ExternalDocumentUpdate } from './html-editor.actions';
import { TableGeneratorDialogComponent } from './table-generator-dialog/table-generator-dialog.component';
import { TagsDefinitionDialogComponent } from './tags-definition-dialog/tags-definition-dialog.component';
import { DocumentWasReset } from '@state/matter/documents/documents.actions';
import {
	ConvertedTags,
	RevertedTags,
} from '@state/global/documents/selected-matter-documents.actions';
import sanitizeHtml from 'sanitize-html';
import { TestHooks } from './html-editor.hooks';

interface DialogData {
	itemNumber: string;
}

@UntilDestroy()
@Component({
	selector: 'app-html-editor',
	templateUrl: './html-editor.component.html',
	standalone: false,
})
export class HtmlEditorComponent implements OnDestroy, OnInit {
	testHooks = TestHooks;
	busy: boolean;
	public Tags: FlattenedCustomTags[];
	private firstUpdate = true;
	@SelectSnapshot(AuthState.getUserId) private userId: string;
	@SelectSnapshot(InstanceState.id) private instanceId!: string;

	@Input() document;
	@Input() context: Context | null;
	@Input() documentId: string;

	@Input() minHeight: string;
	@Input() location: DocumentLocation.BUSINESS | DocumentLocation.MATTER =
		DocumentLocation.MATTER;
	@Input() debounceTime = 1000;
	@Output() onChange: EventEmitter<string>;
	@Output() onStatusChange: EventEmitter<EditorStatus>;
	@Output() workingChange = new EventEmitter<boolean>();

	@Select(TagsSelector.getTags) public tags$: Observable<CustomTags>;
	@SelectSnapshot(BusinessState.getLogoUrl) public logoUrl: string;
	@SelectSnapshot(SelectedMatter.id) public matterId: string;
	@Select(FileUploadState.getFileStatus) public fileUploadStatusFn$: Observable<
		(filter: string) => FileUpload
	>;

	@SelectSnapshot(BusinessState.getDefaultFont) defaultFont: Font;
	@SelectSnapshot(BusinessState.getDefaultLineHeight) defaultLineHeight: number;
	@SelectSnapshot(BusinessState.getDefaultFontSize) defaultFontSize: number;
	@SelectSnapshot(DocumentStylingState.getTinyMceCssStyling) css: string;

	// rome-ignore lint/suspicious/noExplicitAny: <explanation>
	tinyMCEInit: any;
	@Input() disabled = false;
	@Output() triggerSettings: EventEmitter<void>;

	debouncer = new Subject<string>();

	private fileUploadReset$: Subject<void>;
	private editor;

	public dialog = inject(MatDialog);
	private actions$ = inject(Actions);
	private store = inject(Store);

	constructor(
		private businessDispatch: BusinessDispatch,
		private matterDispatch: MatterDispatch,
		private tagsService: TagsService,
		private snackbar: SnackbarService,
	) {
		this.onChange = new EventEmitter<string>();
		this.onStatusChange = new EventEmitter<EditorStatus>();
		this.fileUploadReset$ = new Subject<void>();
		this.triggerSettings = new EventEmitter<void>();
	}

	ngOnInit(): void {
		this.busy = true;

		this.debouncer
			.pipe(
				debounceTime(this.debounceTime),
				distinctUntilChanged(),
				untilDestroyed(this),
			)
			.subscribe((str: string) => {
				this.onChange.emit(str);
			});

		this.initializeEditor();
	}

	ngOnDestroy(): void {
		this.fileUploadReset$.complete();
	}

	// rome-ignore lint/suspicious/noExplicitAny: <explanation>
	editorReady(editorElement: EventObj<any>): void {
		this.busy = false;
		this.onStatusChange.emit(EditorStatus.READY);
		const editor = (this.editor = editorElement.editor);

		this.tagsService
			.getTags()
			.pipe(untilDestroyed(this))
			.subscribe((tags) => {
				this.Tags = tags;
			});

		this.actions$
			.pipe(ofActionSuccessful(ExternalDocumentUpdate))
			.pipe(
				untilDestroyed(this),
				map((doc: ExternalDocumentUpdate) => doc),
				filter((doc) => doc.documentId === this.documentId),
				filter(
					({ document }) =>
						document?.lastEditedByInstanceId !== this.instanceId, // if a document was reset, it was likely reset by someone else
				),
				tap((doc) => {
					// adjust here if updated externally (reset will have no author)
					if (doc.document.lastEditedBy || !doc.document.author) {
						this.document = doc.documentHtml;
					}
				}),
				map((doc) => doc.document?.lastEditedBy || doc?.document?.author), // yoink the author
				switchMap((editedBy) =>
					editedBy === this.userId
						? of(
								'This document was updated by you, in another window.  The changes have been merged in.',
							)
						: editedBy
							? this.store
									.select(BusinessUsersState.userById(editedBy))
									.pipe(
										map(
											(user) =>
												`This document was edited by ${getFullName([user.firstName, user.lastName])}.  The changes have been merged in.`,
										),
									)
							: !editedBy
								? of('The document has been reset.')
								: of('The document has been updated by another user.'),
				),
				distinctUntilChanged(),
			)
			.subscribe((text) =>
				this.snackbar.openSnackBar(
					{
						text,
					},
					{
						duration: 7000,
					},
				),
			);

		// after a successful reset/convert/revert call, lets set our value manually
		merge(
			this.actions$.pipe(ofActionSuccessful(DocumentWasReset)),
			this.actions$.pipe(ofActionSuccessful(ConvertedTags)),
			this.actions$.pipe(ofActionSuccessful(RevertedTags)),
		)
			.pipe(
				untilDestroyed(this),
				map(
					(
						doc:
							| DocumentWasReset
							| ConvertedTags
							| RevertedTags
							| ExternalDocumentUpdate,
					) => doc,
				),
				filter((doc) => doc.documentId === this.documentId),
				tap((doc) => {
					// after each successful tag convert or tag revert action, trigger a snackbar message to provide feedback to the user (snackbar messages for document reset actions are triggered in the section above)
					if (doc instanceof ConvertedTags) {
						this.snackbar.openSnackBar(
							{
								text: 'Tags have been converted.',
							},
							{
								duration: 7000,
							},
						);
					} else if (doc instanceof RevertedTags) {
						this.snackbar.openSnackBar(
							{
								text: 'Tags have been reverted.',
							},
							{
								duration: 7000,
							},
						);
					}
				}),
			)
			.subscribe((document) => (this.document = document.documentHtml));

		// Here we attach the event listener directly
		editorElement.editor?.contentDocument?.addEventListener('click', (evt) => {
			const element = (evt.target as Element)?.closest(
				'.smart-table.mceNonEditable',
			);

			if (element) {
				// Remove the 'clicked' class from all elements with the '.smart-table' class.
				const allSmartTables =
					editorElement.editor.contentDocument.querySelectorAll('.smart-table');
				allSmartTables.forEach((table) => {
					table.classList.remove('clicked');
				});

				// Add the 'clicked' class to the current target element.
				element.classList.add('clicked');

				const classList = element.getAttribute('class');

				const sanatizHtml = editor.getContent();
				const parser = new DOMParser();
				const doc = parser.parseFromString(sanatizHtml, 'text/html');
				const clickedTable = doc.getElementsByClassName('clicked')?.[0];

				this.openLoopTableDialog(
					`<html><table class="${classList}">${clickedTable.innerHTML}</table></html>`,
				);
			}
		});

		editor.ui.registry.addAutocompleter('customtags_cardmenuitems', {
			trigger: '~',
			columns: 1,
			fetch: (pattern, maxResults) => {
				return new Promise((resolve) => {
					const matches = this.Tags.filter(
						(tag) =>
							tag.label.toLowerCase().indexOf(pattern.toLowerCase()) !== -1,
					).slice(0, maxResults);

					return resolve(
						matches.map((tag) => {
							const value =
								tag.value.length > 35
									? `${tag.value.substring(0, 35)}…`
									: tag.value;

							return {
								type: 'cardmenuitem',
								value: `{{${tag.label}}}`,
								label: tag.label,
								items: [
									{
										type: 'cardcontainer',
										direction: 'vertical',
										items: [
											{
												type: 'cardtext',
												text: tag.label,
												name: 'char_name',
											},
											{
												type: 'cardtext',
												text: value || ' ',
											},
										],
									},
								],
							};
						}),
					);
				});
			},
			highlightOn: ['char_name'],
			matches: () => true,
			maxResults: 10,
			minChars: 2,
			onAction: (autocompleteApi, rng, value) => {
				editor.selection.setRng(rng);
				editor.insertContent(value);
				autocompleteApi.hide();
			},
		});
	}

	updateContent(document: string): void {
		if (this.firstUpdate) {
			this.firstUpdate = false;

			return;
		}

		this.debouncer.next(document);
	}

	documentSettings(): void {
		this.triggerSettings.emit();
	}

	tagsDefinition(): void {
		this.dialog
			.open(TagsDefinitionDialogComponent, {
				data: {
					context: this.context,
					location: this.location,
				},
				maxWidth: '1000px',
				panelClass: 'no-padding',
				width: '90%',
				height: '90%',
				maxHeight: '780px',
			})
			.afterClosed()
			.pipe(
				untilDestroyed(this),
				take(1),
				filter((value) => !!value),
			)
			.subscribe((value: string) => {
				this.editor.execCommand('mceInsertContent', false, `{{${value}}}`);
				this.updateContent(this.editor.getContent());
			});
	}

	restartNumbering(editor: Editor): void {
		editor.windowManager.open({
			title: 'Restart Numbering',
			body: {
				type: 'panel',
				items: [
					{
						type: 'input',
						name: 'itemNumber',
						label: 'Item number',
					},
				],
			},
			buttons: [
				{
					type: 'cancel',
					text: 'Close',
				},
				{
					type: 'submit',
					text: 'Save',
					primary: true,
				},
			],
			onSubmit(api) {
				const node = tinymce.activeEditor.selection.getNode();
				// rome-ignore lint/suspicious/noExplicitAny: <explanation>
				const data: DialogData = api.getData() as DialogData;
				node.setAttribute('value', data.itemNumber);
				api.close();
			},
		});
	}

	openLoopTableDialog(html?: string): void {
		// Open the table generator dialog with specified options
		this.dialog
			.open(TableGeneratorDialogComponent, {
				data: {
					html,
				},
				maxWidth: '1200px',
				width: '90%',
			})
			.afterClosed()
			.pipe(untilDestroyed(this), take(1))
			.subscribe((value: string) => {
				if (value) {
					let newValue = value;
					if (html) {
						// If HTML content is provided, replace the clicked table with the new value
						newValue = replaceClickedTable(this.editor.getContent(), value);
						this.editor.setContent(newValue);
						this.updateContent(this.editor.getContent());
					} else {
						// Otherwise, insert the value at the current cursor position
						this.editor.execCommand('mceInsertContent', false, value);
						this.updateContent(this.editor.getContent());
					}
				} else {
					// If no value is returned, remove the 'clicked' class from all tables in the editor content - this makes sure that the next time a table is clicked, it will be brand new
					this.editor.setContent(
						removeClickedFromAllTables(this.editor.getContent()),
					);
				}
			});
	}

	private initializeEditor(): void {
		this.tinyMCEInit = {
			advlist_number_styles:
				'default,lower-alpha,lower-roman,upper-alpha,upper-roman',
			base_url: '/tinymce',
			branding: false,
			license_key: 'gpl',
			browser_spellcheck: true,
			content_style: `
			@import url('https://fonts.googleapis.com/css2?family=Nunito:ital,wght@0,400;0,700;1,400;1,700&display=swap');
			@font-face {
				font-family: 'calibri';
				src: url('/assets/fonts/Calibri.woff2') format('woff2'), url('/assets/fonts/Calibri.woff') format('woff');
			}
			* { font-family: ${FontInfo[this.defaultFont].editorFont}; }
			p, li, td, th { font-size: ${this.defaultFontSize}pt; line-height: ${this.defaultLineHeight}; }
			${this.css}
			.mceNonEditable { background-color: rgb(241, 255, 248); color: rgb(21, 132, 73); font-size: calc(1em - 2px); padding: 1px 3px; }
			table { margin-top: 2px; margin-bottom: 2px; border-spacing: 0px; border-collapse: collapse; border-width: 1px; }
			img { display: inline-block; height: auto; max-width: 100% }
			thead { background-color: #f3f3f3; }
			hr { margin-top: 10px; margin-bottom:10px}
			th { text-align: left; }
			td { vertical-align: top; padding:0px !important;}
			.table_zebra_striped tbody tr:nth-child(even) { background-color: #f6f6f6; }
			`,
			contextmenu: false,
			elementpath: false,
			fix_list_elements: true,
			font_family_formats:
				'Arial=arial; Calibri=calibri; Courier New=courier,courier new; Nunito=nunito; Helvetica=helvetica; Roboto=Roboto, sans-serif; Tahoma=tahoma; Times New Roman=times,times new roman;',
			font_size_formats:
				'5pt 6pt 7pt 8pt 9pt 10pt 11pt 12pt 13pt 14pt 15pt 16pt 17pt 18pt 19pt 20pt 21pt 22pt 23pt 24pt 36pt 48pt',
			forced_root_block: 'p',
			formats: {
				alignleft: {
					selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,table',
					styles: { textAlign: 'left' },
				},
				aligncenter: {
					selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,table',
					styles: { textAlign: 'center' },
				},
				alignright: {
					selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,table',
					styles: { textAlign: 'right' },
				},
			},
			height: this.minHeight || '100%',
			highlight_on_focus: false,
			image_advtab: false,
			image_uploadtab: true,
			images_file_types: 'jpeg,jpg,jpe,png,gif',
			images_reuse_filename: true,
			images_upload_handler: (blobInfo, progress) => {
				return new Promise((resolve, reject) => {
					this.busy = true;
					this.onStatusChange.emit(EditorStatus.SAVING);
					this.fileUploadReset$.next();

					const DocId = createId();

					this.fileUploadStatusFn$
						.pipe(
							untilDestroyed(this),
							takeUntil(this.fileUploadReset$),
							switchMap((filterFn) => of(filterFn(DocId))),
							filter((status) => !!status),
							filter(
								(status) =>
									status.status === UploadFileStatus.SUCCESS ||
									status.status === UploadFileStatus.FAILED,
							),
						)
						.subscribe((status) => {
							switch (status.status) {
								case UploadFileStatus.FAILED:
									reject(status.error);
									break;
								case UploadFileStatus.SUCCESS:
									progress(100);
									resolve(status.publicUrl);
									break;
							}

							this.busy = false;
							this.onStatusChange.emit(EditorStatus.READY);
						});

					switch (this.location) {
						case DocumentLocation.BUSINESS:
							this.businessDispatch.UploadBusinessFile(
								DocId,
								blobInfo.filename(),
								blobInfo.blob(),
							);
							break;
						case DocumentLocation.MATTER:
							this.matterDispatch.UploadMatterFile(
								DocId,
								this.matterId,
								blobInfo.filename(),
								blobInfo.blob(),
								MatterDocumentType.DOCUMENT_IMAGE,
							);
							break;
					}
				});
			},
			indent_use_margin: true,
			line_height_formats: '1 1.1 1.15 1.2 1.3 1.4 1.5 1.6 2 2.5 3.0',
			lists_indent_on_tabs: true,
			menubar: false,
			noneditable_class: 'mceNonEditable',
			noneditable_regexp: /\{?\{\{[^}]+\}\}\}?/gm, // matches {{handlebar}} and {{{handlebar}}}
			format_noneditable_selector: 'span', // This makes it so that tags can be styled with the toolbar (e.g. bold, italic, etc.) but not edited
			pagebreak_separator: '<span class="pdf-pagebreak-before">&nbsp;</span>',
			pagebreak_split_block: true,
			paste_block_drop: true,
			paste_data_images: true,
			plugins: 'lists advlist image table code pagebreak',
			resize: false,
			readonly: this.disabled,
			paste_preprocess: (editor, args) => {
				console.log({ args });
				// Targetting content copied from outside the current tab (e.g. from another tab in the same browser window or MS Word)
				if (!args.internal) {
					// Tries to remove all MSO classes from the pasted content
					// Styles in tables are kept to maintain the table structure and cell sizes
					args.content = sanitizeHtml(args.content, {
						allowedAttributes: {
							td: ['colspan', 'rowspan', 'style', 'data-*'],
							th: ['colspan', 'rowspan', 'style', 'data-*'],
							table: ['style', 'class', 'data-*', 'data-*'],
							tr: ['style', 'data-*'],
							p: ['style'],
							span: ['style'],
						},
						allowedClasses: {
							table: [
								'noBorders',
								'table_no_style',
								'table_zebra_striped',
								'table',
								'smart-table',
								'clicked',
								'mceNonEditable',
							],
						},
						allowedStyles: {
							'*': {
								width: [/./g],
								color: [/./g],
								'font-size': [/./g],
								'background-color': [/./g],
								'text-decoration': [/./g],
							},
							table: {
								'border-collapse': [/./g],
								'border-spacing': [/./g],
								width: [/./g],
								border: [/./g],
								'border-color': [/./g],
							},
						},
					});
				}
			},
			setup: (editor: Editor) => {
				// note - these icons are NOT fa- icons, but from the editor's library itself
				editor.ui.registry.addButton('documentSetupButton', {
					icon: 'preferences',
					onAction: () => this.documentSettings(),
					tooltip: 'Settings',
				});
				editor.ui.registry.addButton('restartNumbering', {
					icon: 'edit-block',
					onAction: () => this.restartNumbering(editor),
					tooltip: 'Restart numbering',
				});
				editor.ui.registry.addButton('tagsDefinitionButton', {
					icon: 'code-sample',
					onAction: () => this.tagsDefinition(),
					text: 'Tags',
				});
				editor.ui.registry.addButton('tagsTableButton', {
					icon: 'code-sample',
					onAction: () => this.openLoopTableDialog(),
					text: 'Smart Table',
				});

				// Listen for paste event
				editor.on('paste cut', () => {
					this.firstUpdate = false;
					editor.setDirty(true);
				});

				editor.on('NodeChange', (e) => {
					// Trigger save on image upload to refresh browser
					if (e.element.nodeName == 'IMG') {
						this.editor.save();
					}
				});

				// editor.on('TableModified', (e) => {
				// 	// Hack: Fixes border style 'none' not removing border from table and not propagating to cells
				// 	// Checking if the style has been set to 'none' and removing the border color if it has
				// 	if(e.table.style.border.includes('none')) {

				// 		e.table.style.borderColor = 'none';
				// 		e.table.removeAttribute('border');
				// 		e.table.style.border = null;
				// 		e.table.style.borderColor = null;
				// 		e.table.classList.add('mce-item-table');

				// 		Array.from(e.table.getElementsByTagName('td')).forEach((td) => {
				// 			td.style.border = null;
				// 			td.style.borderColor = null;
				// 		})
				// 		this.editor.save();
				// 	}
				// })

				// editor.on('keydown',(event)=>{
				// 	const currentNode = editor.selection.getNode();
				// 	 const range = editor.selection.getRng();

				// 	if (event.key == 'Tab' && currentNode.tagName == 'P') {

				// 		if (event.shiftKey) {
				// 			const previousSibling = range.startContainer.previousSibling as Element;

				// 			if(previousSibling.tagName == 'SPAN' && previousSibling.className == 'tab-span') {
				// 				previousSibling.remove();
				// 			}
				// 		}
				// 		else {
				// 			editor.execCommand('InsertHTML', false, `<span class="tab-span" contenteditable="false">&emsp;</span`)
				// 		}

				// 		event.preventDefault();
				// 		return false;
				// 	  }
				// })
			},
			statusbar: false,
			suffix: '.min',
			table_advtab: false,
			table_appearance_options: true,
			// table_border_styles: [
			// 	{ title: 'Solid', value: 'solid' },
			// 	{ title: 'Inset', value: 'inset' },
			// 	{ title: 'Double', value: 'double' },
			// 	{ title: 'Outset', value: 'outset' },
			// 	{ title: 'None', value: 'none' },
			// 	{ title: 'Hidden', value: 'hidden' }
			// ],
			table_border_widths: [
				{ title: '1px', value: '1px' },
				{ title: '2px', value: '2px' },
				{ title: '3px', value: '3px' },
				{ title: '4px', value: '4px' },
				{ title: '5px', value: '5px' },
			],
			table_cell_advtab: false,
			table_class_list: [
				{
					title: 'None',
					value: 'table_no_style',
				},
				{
					title: 'Zebra striped',
					value: 'table_zebra_striped',
				},
			],
			table_default_attributes: {},
			table_default_styles: {
				border: 'none',
				'border-width': '1px',
				width: '100%',
				'word-break': 'break-all',
			},
			table_header_type: 'sectionCells',
			table_row_advtab: true,
			table_sizing_mode: 'relative',
			table_style_by_css: true,
			table_use_colgroups: false,
			text_patterns: [
				{ start: '*', end: '*', format: 'italic' },
				{ start: '**', end: '**', format: 'bold' },
				{ start: '#', format: 'h1', trigger: 'space' },
				{ start: '##', format: 'h2', trigger: 'space' },
				{ start: '###', format: 'h3', trigger: 'space' },
				{ start: '####', format: 'h4', trigger: 'space' },
				{ start: '#####', format: 'h5', trigger: 'space' },
				{ start: '######', format: 'h6', trigger: 'space' },
				{ start: '1.', cmd: 'InsertOrderedList', trigger: 'space' },
				{ start: '*', cmd: 'InsertUnorderedList', trigger: 'space' },
				{ start: '-', cmd: 'InsertUnorderedList', trigger: 'space' },
				{ start: '---', cmd: 'InsertHorizontalRule', trigger: 'space' },
				{ start: '--', replacement: '—' },
				{ start: '(c)', replacement: '©' },
			],
			toolbar: [
				{
					items: ['undo', 'redo'],
					name: 'history',
				},
				{
					items: ['tagsDefinitionButton'],
					name: 'tagsDefinitionButton',
				},
				{
					items: ['tagsTableButton'],
					name: 'tagsTableButton',
				},
				{
					items: ['fontsize'],
					name: 'font',
				},
				{
					items: ['blocks', 'forecolor'],
					name: 'fontLook',
				},
				{
					items: ['bold', 'italic', 'underline', 'strikethrough'],
					name: 'style',
				},
				{
					items: ['removeformat'],
					name: 'override',
				},
				{
					items: ['lineheight'],
					name: 'adjustment',
				},
				{
					items: ['alignleft', 'aligncenter', 'alignright', 'alignjustify'],
					name: 'alignment',
				},
				{
					items: ['hr', 'pagebreak'],
					name: 'page',
				},
				{
					items: ['image'],
					name: 'image',
				},
				{
					items: ['outdent', 'indent'],
					name: 'format',
				},
				{
					items: ['bullist', 'numlist'],
					name: 'lists',
				},
				{
					items: ['table'],
					name: 'table',
				},
				{
					items: ['code'],
					name: 'code',
				},
				{
					items: ['documentSetupButton'],
					name: 'documentSetupButton',
				},
			],
			toolbar_mode: 'wrap',
			toolbar_sticky: true,
		};
	}
}
